import { Outlet, useRouterState } from '@tanstack/react-router';
import { Menu, Search, X } from 'lucide-react';
import { motion } from 'motion/react';
import { useEffect, useState } from 'react';

import { springs } from '@/shared/lib/motion';
import { useSidebar } from '@/shared/store/sidebar';
import { useCurrentWorkspace } from '@/shared/workspace/useCurrentWorkspace';
import { NotificationsBell } from '@/features/notifications/NotificationsBell';
import { FileViewerHost } from '@/features/files/FileViewer';
import { CreateSpaceDialog } from '@/features/space/CreateSpaceDialog';
import { setCreateSpaceOpen, useCreateSpaceOpen } from '@/features/space/createSpaceStore';
import { ConnectionBar } from '@/features/presence/ConnectionBar';
import { CommandPalette, openPalette } from '@/features/search';
import { OnlineNow } from '@/features/presence/OnlineNow';
import { useInboxLive } from '@/features/notifications/useInbox';
import { t } from '@/shared/i18n';
import { Button } from '@/shared/ui/Button';
import { Toaster } from '@/shared/ui/Toast';
import { TooltipProvider } from '@/shared/ui/Tooltip';

import { Backdrop } from './Backdrop';
import { Brand } from './Brand';
import { Sidebar } from './Sidebar';
import styles from './AppShell.module.scss';

/**
 * Оболочка приложения: боковая колонка, верхняя полоса, содержимое.
 *
 * На широком экране колонка стоит слева всегда. На узком она прячется и
 * выезжает панелью по кнопке — на резиновой пружине, как всё, что переезжает.
 * Верхняя полоса — только поиск (⌘K, фаза 7) и колокольчик (фаза 2): пока
 * это заглушки, но место под них занято сразу, чтобы экраны не прыгали
 * потом.
 */
// Подпись клавиши в кнопке поиска: ⌘ на Mac, Ctrl на остальных.
const modKey =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl+';

export function AppShell() {
  const collapsed = useSidebar((state) => state.collapsed);
  const { slug, workspace } = useCurrentWorkspace();

  // ⌘K / Ctrl+K — палитра поиска и действий, из любого места приложения.
  useEffect(() => {
    if (slug === '') {
      return;
    }

    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openPalette();
      }
    };

    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [slug]);
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // Панель помнит адрес, на котором её открыли, и открыта, только пока он
  // тот же: любой переход закрывает её сам — не только по ссылкам
  // сайдбара, но и из дерева страниц или переключателя пространств, которые
  // ходят через navigate и про панель не знают. Без эффекта и setState.
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const drawerOpen = openedAt === pathname;
  const setDrawerOpen = (open: boolean) => {
    setOpenedAt(open ? pathname : null);
  };

  // Живые входящие: счётчик и «(N)» в заголовке вкладки — на всех страницах.
  useInboxLive(slug);

  // Диалог создания проекта — здесь, а не в сайдбаре (см. createSpaceStore).
  // Открылся — выдвижная панель закрывается, чтобы не мешать.
  const creatingSpace = useCreateSpaceOpen();
  // Выдвижная панель считается открытой, только пока не открыт диалог.
  const drawerShown = drawerOpen && !creatingSpace;

  // Esc закрывает панель; пока она открыта, страница под ней не крутится.
  useEffect(() => {
    if (!drawerShown) {
      return;
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenedAt(null);
      }
    };

    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [drawerShown]);

  return (
    <TooltipProvider delayDuration={400}>
      <div className={styles['shell']}>
        <Backdrop />

        {/* Ширина колонки анимируется резиновой пружиной; содержимое
            справа подтягивается вместе с ней — это одна layout-операция. */}
        <motion.aside
          className={styles['rail']}
          initial={false}
          animate={{ width: collapsed ? 64 : 248 }}
          transition={springs.rubber}
        >
          <Sidebar />
        </motion.aside>

        {/* Выдвижная панель на телефоне — без анимации: пружина на
            transform поверх размытого фона заметно грузит телефон (Давид,
            14 сентября 2026). Полный проход по мобильной версии — после PWA. */}
        {drawerShown && (
          <>
            <button
              type="button"
              className={styles['scrim']}
              aria-label={t.nav.closeMenu}
              onClick={() => {
                setDrawerOpen(false);
              }}
            />
            <aside className={styles['drawer']}>
              <Sidebar
                collapsible={false}
                onNavigate={() => {
                  setDrawerOpen(false);
                }}
              />
              <button
                type="button"
                className={styles['drawer__close']}
                aria-label={t.nav.closeMenu}
                onClick={() => {
                  setDrawerOpen(false);
                }}
              >
                <X />
              </button>
            </aside>
          </>
        )}

        <div className={styles['main']}>
          <header className={styles['topbar']}>
            <Button
              iconOnly
              variant="ghost"
              tone="neutral"
              size="sm"
              className={styles['topbar__menu']}
              aria-label={t.nav.openMenu}
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <Menu />
            </Button>
            <span className={styles['topbar__brand']}>
              <Brand compact />
            </span>

            {slug !== '' && (
              <button type="button" className={styles['search']} onClick={openPalette}>
                <Search />
                <span className={styles['search__text']}>{t.nav.searchHint}</span>
                <kbd className={styles['search__kbd']}>{modKey}K</kbd>
              </button>
            )}

            <span className={styles['topbar__spacer']} />

            {slug !== '' && (
              <Button
                iconOnly
                variant="ghost"
                tone="neutral"
                size="sm"
                className={styles['search-button']}
                aria-label={t.palette.title}
                onClick={openPalette}
              >
                <Search />
              </Button>
            )}

            {slug !== '' && (
              <span className={styles['topbar__online']}>
                <OnlineNow workspace={slug} workspaceID={workspace?.id} />
              </span>
            )}
            {slug !== '' && <NotificationsBell workspace={slug} />}
          </header>

          <ConnectionBar />

          {slug !== '' && <CommandPalette workspace={slug} />}

          <main className={styles['content']}>
            <Outlet />
          </main>
        </div>

        {/* Тосты живут в оболочке, а не на экране: об ошибке запроса надо
            сообщить и там, где экрана ещё или уже нет. */}
        <Toaster />
        <FileViewerHost />
        {slug !== '' && (
          <CreateSpaceDialog
            workspace={slug}
            open={creatingSpace}
            onOpenChange={setCreateSpaceOpen}
          />
        )}
      </div>
    </TooltipProvider>
  );
}
